import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { isEmpty } from "lodash";

import type {
    IEquipmentFunctionalityTypeSlim,
    IEquipmentType,
    IManufacturer,
    IResponseWithLoading,
    IrMap,
} from "App/Modules/Models";
import { EquipmentFunctionalityClass, EquipmentFunctionalityClassFunctions } from "App/Modules/Models";
import type {
    IEquipmentFunctionalityTypesRepository,
    IEquipmentTypeRepository,
    IManufacturersRepository,
} from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class AddFunctionalityComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "irEquipmentFunctionalityTypesRepository",
        "mmEquipmentTypeRepository",
        "irManufacturersRepository",
    ];

    public resolve: IFormContainerResolve<IEquipmentFunctionalityTypeSlim, IEquipmentType>;

    public addResponse: IResponseWithLoading<IEquipmentFunctionalityTypeSlim>;

    public functionality: IEquipmentFunctionalityTypeSlim;

    public equipmentType: IEquipmentType;

    public manufacturers: IResponseWithLoading<IManufacturer[]>;

    public equipmentFunctionalityClasses: IrMap<EquipmentFunctionalityClass, string>;

    public state: number;

    constructor(
        private readonly $scope: IScope,
        private readonly equipmentFunctionalityTypesRepository: IEquipmentFunctionalityTypesRepository,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
        private readonly manufacturersRepository: IManufacturersRepository,
    ) {}

    public $onInit() {
        this.equipmentType = this.resolve.bindings;
        this.functionality = {
            class: EquipmentFunctionalityClass.Video,
        } as IEquipmentFunctionalityTypeSlim;
        this.state = 0;
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public selectFunctionality(functionality: IEquipmentFunctionalityTypeSlim) {
        this.functionality = functionality;
        this.addFunctionality();
    }

    public createNewFunctionality(searchString: string) {
        this.functionality = {
            class: EquipmentFunctionalityClass[EquipmentFunctionalityClass.Video] as any as EquipmentFunctionalityClass,
            manufacturer: this.equipmentType.manufacturer,
            model: searchString,
        } as IEquipmentFunctionalityTypeSlim;
        this.nextStep();
    }

    public nextStep() {
        this.state = 1;

        if (this.functionality.id) {
            this.addFunctionality();
        } else {
            extendWithLoadingAndFail(this.manufacturersRepository.getManufacturers())
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<IManufacturer[]>) => {
                        this.manufacturers = response;
                    },
                    (error) => {
                        this.manufacturers = error;
                    },
                );

            this.equipmentFunctionalityClasses =
                EquipmentFunctionalityClassFunctions.GetEquipmentFunctionalityClassDictionary();
        }
    }

    public CreateAndAddFunctionality() {
        this.addResponse = { isLoading: true };
        extendWithLoadingAndFail(this.equipmentFunctionalityTypesRepository.addFunctionalityType(this.functionality))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IEquipmentFunctionalityTypeSlim>) => {
                    this.addResponse = response;
                    if (!response.isLoading && response.data) {
                        this.functionality = response.data;
                        this.addFunctionality();
                    }
                },
                (error) => {
                    this.addResponse = error;
                },
            );
    }

    public addFunctionality() {
        if (!isEmpty(this.functionality)) {
            this.addResponse = { isLoading: true };
            extendWithLoadingAndFail(
                this.equipmentTypeRepository.addFunctionality(this.equipmentType.id, this.functionality),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<IEquipmentFunctionalityTypeSlim>) => {
                        this.addResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.addResponse = error;
                    },
                );
        }
    }
}
